// src/views/front/checkout/CheckoutPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import CartProductListSidebar from './component/CartProductListSidebar';
import { useSelector, useDispatch } from 'react-redux';
import { updateOrderTable, removeOrderTable, authLogin } from '@/store/actions';
import { setCredit } from '@/store/creditSlice';
import { useNavigate, useParams } from 'react-router-dom';
import validationErrors from '@/lib/validationErrors';
import { addTableOrderService } from '@/services/OrderServices';
import { getTableBySlugDiningTableService } from '@/services/DiningTableServices';
import {
  getTablePaymentService,
  getWaffiPaymentService,
  getEdahabPaymentService,
  getPremierPaymentService,
  getRaamsPaymentService,
  getRaamsGatewaysService,
} from '@/services/PaymentService';
import { getCustomerProfileService } from '@/services/CustomerServices';
import { Input } from '@/components/ui/inputs/input';
import { InputSuffix } from '@/components/ui/inputs/input-suffix';
import { useForm, Controller } from 'react-hook-form';
import ToastMe from '@/components/ui/ToastMe';
import { setCompanyId } from '@/security/Axios';

const CREDIT_GATEWAY_SLUG = 'credit';
const EXCLUDED_GATEWAY_SLUGS = new Set(['cash_on_delivery', 'cod', 'cash']);
const RAAMS_ALLOWED_GATEWAY_IDS = new Set(['12', '13', '14', '16', '21']);
const FRONTEND_GATEWAY_SETTING_MAP = {
  waafi: 'frontend_show_waafi',
  edahab: 'frontend_show_edahab',
  premier_wallets: 'frontend_show_premier_wallets',
};

/** Phone sanitizing helper */
const sanitizeAccountNumber = (raw) => {
  let v = String(raw || '').replace(/\D+/g, '');
  if (v.startsWith('0')) v = v.slice(1);
  if (v.length > 9) v = v.slice(0, 9);
  return v;
};
const isNineDigits = (v) => /^\d{9}$/.test(String(v || ''));

const filterVisibleRaamsGateways = (list = []) => {
  const filtered = list.filter((gateway) =>
    RAAMS_ALLOWED_GATEWAY_IDS.has(String(gateway?.id)),
  );
  return filtered.length ? filtered : list;
};

const isFrontendGatewayVisible = (gateway, settings) => {
  const key = FRONTEND_GATEWAY_SETTING_MAP[String(gateway?.slug || '')];
  if (!key) return true;
  const value = settings?.[key];
  return value === undefined || value === null || value === '' || String(value) !== '0';
};

const CheckoutPage = () => {
  /* ─── Redux state ──────────────────────────────────────────────── */
  const { tableOrder, generalSetting } =
    useSelector((state) => state?.GeneralSetting || {});
  const creditSlice = useSelector((state) => state.Credit || {});
  const authUser = useSelector((state) => state.Auth?.userData);
  const authUserId = authUser?._id;
  const cartItems = useSelector((state) => state.Cart?.items || []);

  const { enabled: creditEnabled, balance: creditBalance } = creditSlice;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { slug } = useParams();

  /* ─── Ensure tenant header exists ASAP ─────────────────────────── */
  useEffect(() => {
    const cid = localStorage.getItem('company_id') || '64b17c2f1e34a5d9bdf10c01';
    if (cid) setCompanyId(cid);
  }, []);

  /* ─── Hydrate Auth/Credit from customer JWT on refresh ─────────── */
  useEffect(() => {
    const jwt =
      localStorage.getItem('customer_jwt') ||
      localStorage.getItem('customerToken');

    if (!jwt || authUserId) return; // nothing to do

    dispatch(getCustomerProfileService()).then((res) => {
      const c = res?.data?.data;
      if (!c?._id) return;

      dispatch(
        authLogin({
          data: { token: jwt, data: c },
          message: 'ok',
          success: true,
        }),
      );
      dispatch(
        setCredit({
          enabled: !!c.creditEnabled,
          balance:
            (Number(c.creditLimit) || 0) - (Number(c.outstandingCredit) || 0),
          limit: Number(c.creditLimit) || 0,
        }),
      );
    });
  }, [dispatch, authUserId]);

  /* ─── Also re-sync Credit slice whenever authUser changes ──────── */
  useEffect(() => {
    if (!authUser) return;
    const limit = Number(authUser.creditLimit || 0);
    const used = Number(authUser.outstandingCredit || 0);
    dispatch(
      setCredit({
        enabled: !!authUser.creditEnabled,
        balance: Math.max(0, limit - used),
        limit,
      }),
    );
  }, [authUser, dispatch]);

  /* ─── Form (react-hook-form) ───────────────────────────────────── */
  const {
    setValue,
    formState: { errors },
    handleSubmit,
    control,
  } = useForm();

  /* ─── Local UI state ───────────────────────────────────────────── */
  const [isLoading, setIsLoading] = useState(false);
  const [tableDataSource, setTableDataSource] = useState();
  const [paymentGatewayOptionList, setPaymentGatewayOptionList] = useState([]);
  const [paymentMethodId, setPaymentMethodId] = useState();
  const [paymentMethodSlug, setPaymentMethodSlug] = useState();
  const [isFormSumbmit, setIsFormSumbmit] = useState(false);
  const [isCreditPayment, setIsCreditPayment] = useState(false);
  const [raamsGateways, setRaamsGateways] = useState([]);
  const [raamsGatewayId, setRaamsGatewayId] = useState('');
  const [isRaamsGatewayLoading, setIsRaamsGatewayLoading] = useState(false);

  /* ─── Derived values ───────────────────────────────────────────── */
  const hasCustomerJwt = !!(
    localStorage.getItem('customer_jwt') || localStorage.getItem('customerToken')
  );
  const isLoggedIn = !!(authUserId || hasCustomerJwt);

  const creditEnabledForUser =
    isLoggedIn && (authUser?.creditEnabled || creditEnabled === true);

  const numericBalance =
    creditEnabledForUser
      ? Number(
          typeof creditBalance === 'number'
            ? creditBalance
            : (authUser?.creditLimit ?? 0) - (authUser?.outstandingCredit ?? 0),
        ) || 0
      : 0;

  const numericTotal = Number(tableOrder?.total) || 0;
  const creditAllowed = creditEnabledForUser && numericBalance >= numericTotal;

  /* ─── Data fetchers ────────────────────────────────────────────── */
  const getDataBySlug = useCallback(
    (slugParam) => {
      setIsLoading(true);
      dispatch(getTableBySlugDiningTableService(slugParam))
        .then((res) => {
          const tbl = res?.data?.data;
          setTableDataSource(tbl);
          dispatch(
            updateOrderTable({
              preparation_time: generalSetting?.site_food_preparation_time,
              order_type: 2,
              service_mode: 1,
              dining_table_id: tbl?._id,
            }),
          );
          const cId = String(tbl?.company_id?._id || tbl?.company_id || '');
          if (cId) {
            localStorage.setItem('company_id', cId);
            setCompanyId(cId);
          }
        })
        .catch((err) => {
          const { message, errorData } = err || {};
          validationErrors(errorData, message);
        })
        .finally(() => setIsLoading(false));
    },
    [dispatch, generalSetting],
  );

  const getPaymentGateways = useCallback(() => {
    setIsLoading(true);
    dispatch(getTablePaymentService())
      .then((res) => {
        const list = (res?.data?.data || []).filter((gateway) =>
          isFrontendGatewayVisible(gateway, generalSetting),
        );
        setPaymentGatewayOptionList(list);

        // Only exclude COD-like gateways from the list itself.
        const pickable = list.filter((gw) => !EXCLUDED_GATEWAY_SLUGS.has(gw.slug));

        // Prefer credit ONLY if it's allowed; otherwise pick first non-credit.
        const creditGw = pickable.find((g) => g.slug === CREDIT_GATEWAY_SLUG);
        const firstNonCredit =
          pickable.find((g) => g.slug !== CREDIT_GATEWAY_SLUG) || pickable[0] || list[0];

        const preferred = creditAllowed && creditGw ? creditGw : firstNonCredit;

        if (preferred) {
          setPaymentMethodId(preferred._id);
          setPaymentMethodSlug(preferred.slug);
          setValue('payment_method_id', preferred._id);
          dispatch(updateOrderTable({ payment_method_id: preferred._id }));
          setIsCreditPayment(preferred.slug === CREDIT_GATEWAY_SLUG && creditAllowed);
        }
      })
      .catch((err) => {
        const { message, errorData } = err || {};
        validationErrors(errorData, message);
      })
      .finally(() => setIsLoading(false));
  }, [dispatch, setValue, creditAllowed, generalSetting]);

  const getRaamsGateways = useCallback(() => {
    setIsRaamsGatewayLoading(true);
    dispatch(getRaamsGatewaysService())
      .then((res) => {
        const list = filterVisibleRaamsGateways(res?.data?.data || []);
        setRaamsGateways(list);
        if (list?.length) {
          const firstId = String(list[0].id);
          setRaamsGatewayId((prev) => prev || firstId);
          setValue('raams_gateway_id', firstId);
        }
      })
      .catch((err) => {
        const { message, errorData } = err || {};
        validationErrors(errorData, message);
      })
      .finally(() => setIsRaamsGatewayLoading(false));
  }, [dispatch, setValue]);

  /* ─── Effects ──────────────────────────────────────────────────── */
  useEffect(() => {
    getPaymentGateways();
  }, [getPaymentGateways]);

  useEffect(() => {
    if (slug) getDataBySlug(slug);
  }, [slug, getDataBySlug]);

  useEffect(() => {
    if (paymentMethodSlug === 'raams' && !raamsGateways.length && !isRaamsGatewayLoading) {
      getRaamsGateways();
    }
  }, [paymentMethodSlug, raamsGateways.length, isRaamsGatewayLoading, getRaamsGateways]);

  // If selected gateway becomes invalid (ex: COD), fall back
  useEffect(() => {
    const selectedIsExcluded = EXCLUDED_GATEWAY_SLUGS.has(paymentMethodSlug || '');
    if (selectedIsExcluded) {
      const fallback =
        paymentGatewayOptionList.find((gw) => !EXCLUDED_GATEWAY_SLUGS.has(gw.slug)) ||
        paymentGatewayOptionList[0];

      if (fallback) {
        setPaymentMethodId(fallback._id);
        setPaymentMethodSlug(fallback.slug);
        setValue('payment_method_id', fallback._id);
        dispatch(updateOrderTable({ payment_method_id: fallback._id }));
      }
      setIsCreditPayment(false);
    }
  }, [paymentMethodSlug, paymentGatewayOptionList, dispatch, setValue]);

  // If credit becomes NOT allowed while selected, auto-fallback to non-credit
  useEffect(() => {
    if (!creditAllowed && paymentMethodSlug === CREDIT_GATEWAY_SLUG) {
      const fallback =
        paymentGatewayOptionList.find(
          (gw) => !EXCLUDED_GATEWAY_SLUGS.has(gw.slug) && gw.slug !== CREDIT_GATEWAY_SLUG,
        ) || paymentGatewayOptionList.find((gw) => !EXCLUDED_GATEWAY_SLUGS.has(gw.slug)) || paymentGatewayOptionList[0];

      if (fallback) {
        setPaymentMethodId(fallback._id);
        setPaymentMethodSlug(fallback.slug);
        setValue('payment_method_id', fallback._id);
        dispatch(updateOrderTable({ payment_method_id: fallback._id }));
      }
      setIsCreditPayment(false);
    }
  }, [
    creditAllowed,
    paymentMethodSlug,
    paymentGatewayOptionList,
    dispatch,
    setValue,
  ]);

  /* ─── UI handlers ──────────────────────────────────────────────── */
  const handleOnChange = async (name, value) => {
    if (name !== 'payment_method_id') {
      const normalized = sanitizeAccountNumber(value);
      setValue('account_number', normalized);
      dispatch(updateOrderTable({ account_number: normalized }));
      return;
    }

    const gw = paymentGatewayOptionList.find((pg) => pg._id === value);
    if (!gw) return;

    if (EXCLUDED_GATEWAY_SLUGS.has(gw.slug)) {
      ToastMe('This payment method is not available', 'error');
      return;
    }

    if (gw.slug === CREDIT_GATEWAY_SLUG) {
      if (!creditAllowed) {
        ToastMe('Credit is not available for this account/amount.', 'error');
        return;
      }
      setIsCreditPayment(true);
    } else {
      setIsCreditPayment(false);
    }

    // Clear phone when switching gateways to avoid stale input
    setValue('account_number', '');
    dispatch(updateOrderTable({ account_number: '' }));
    setRaamsGatewayId('');
    setValue('raams_gateway_id', '');

    setPaymentMethodId(value);
    setPaymentMethodSlug(gw.slug);
    setValue('payment_method_id', value);
    dispatch(updateOrderTable({ payment_method_id: value }));

    if (gw.slug === 'raams') {
      getRaamsGateways();
    }
  };

  const handleRaamsGatewayChange = (gatewayId) => {
    const value = String(gatewayId || '');
    setRaamsGatewayId(value);
    setValue('raams_gateway_id', value);
  };

  /* ─── Submit / place order ─────────────────────────────────────── */
  const addTableOrder = (accountNumber) => {
    if (!tableOrder?.total || tableOrder.total < 0.1) {
      ToastMe('Please add a product to the cart first.', 'error');
      return;
    }
    setIsFormSumbmit(true);

    if (isCreditPayment) {
      const jwt =
        localStorage.getItem('customer_jwt') || localStorage.getItem('customerToken');
      if (!(authUserId || jwt)) {
        ToastMe('Please log in to use credit', 'error');
        setIsFormSumbmit(false);
        return;
      }

      const creditOrderPayload = {
        ...tableOrder,
        user_id: authUserId, // server uses this
        payment_method_id: paymentMethodId,
        cartItems: tableOrder?.cartItems ?? cartItems,
      };

      dispatch(addTableOrderService(creditOrderPayload))
        .then((res) => {
         dispatch(removeOrderTable()); // clear cart after order is created
          localStorage.setItem('paymentByCredit', 'true');
          if (typeof creditBalance === 'number') {
            const newBalance = Math.max(0, creditBalance - tableOrder.total);
            dispatch(
              setCredit({
                enabled: newBalance > 0,
                balance: newBalance,
              }),
            );
          }
          navigate(`/menu/table-order/${slug}/${res?.data?.data?._id}`);
        })
        .catch((err) => {
          const { message, errorData } = err || {};
          validationErrors(errorData, message);
            dispatch(removeOrderTable());
        })
        .finally(() => setIsFormSumbmit(false));

      return;
    }

    // Non-credit: require valid phone
    dispatch(addTableOrderService(tableOrder))
      .then((res) => paymentGatewayRedirect(res?.data?.data?._id, accountNumber))
      .catch((err) => {
        const { message, errorData } = err || {};
        validationErrors(errorData, message);
      })
      .finally(() => setIsFormSumbmit(false));
  };

  const onSubmit = (formData) => {
    if (isCreditPayment) {
      addTableOrder('');
      return;
    }
    if (paymentMethodSlug === 'raams' && !raamsGatewayId) {
      ToastMe('Please select a Raams gateway', 'error');
      return;
    }
    const normalized = sanitizeAccountNumber(formData.account_number || '');
    if (isNineDigits(normalized) && formData.payment_method_id) {
      addTableOrder(normalized);
    } else {
      ToastMe('Please enter a valid 9-digit mobile number (no leading 0)', 'error');
    }
  };

  const paymentGatewayRedirect = (orderId, account_number) => {
    if (!paymentMethodId) {
      ToastMe('Payment Gateway is not selected', 'error');
      return;
    }
    const gw = paymentGatewayOptionList.find((pg) => pg._id === paymentMethodId);
    if (!gw) return;

    const clean = sanitizeAccountNumber(account_number);
    const data = { order_id: orderId, account_number: clean };
    const paymentThunk =
      gw.slug === 'raams'
        ? getRaamsPaymentService({ ...data, gateway_id: Number(raamsGatewayId) })
        : gw.slug === 'premier_wallets'
        ? getPremierPaymentService({ ...data, account_number: '00252' + clean })
        : gw.slug === 'edahab'
        ? getEdahabPaymentService(data)
        : getWaffiPaymentService(data);

    setIsLoading(true);
    dispatch(paymentThunk)
      .then((res) => {
        dispatch(removeOrderTable());
        if (gw.slug === 'edahab' && res?.data?.data?.invoic_id) {
          window.location.href =
            'https://edahab.net/API/Payment?invoiceId=' + res.data.data.invoic_id;
        } else if (gw.slug === 'raams' && res?.data?.data?.requiresRedirect && res?.data?.data?.redirect) {
          window.location.href = res.data.data.redirect;
        } else if (gw.slug === 'raams') {
          navigate(`/menu/payment/${slug}/${orderId}?gateway=raams`);
        } else {
          navigate(`/menu/table-order/${slug}/${orderId}`);
        }
      })
      .catch((err) => {
        const { message, errorData } = err || {};
        validationErrors(errorData, message);
         dispatch(removeOrderTable());
        navigate(`/menu/table-order/${slug}/${orderId}`);
      })
      .finally(() => setIsLoading(false));
  };

  /* ─── JSX ─────────────────────────────────────────────────────── */
  return (
    <section className="pt-8 pb-16 min-h-[800px]">
      <div className="container max-w-[965px]">
        <button
          type="button"
          onClick={() => navigate(`/menu/table/${slug}`)}
          className="text-xs font-medium inline-flex mb-3 items-center gap-2 text-primary"
        >
          <i className="lab lab-undo lab-font-size-16" />
          <span>Back to home</span>
        </button>

        <div className="row">
          {/* Left */}
          <div className="col-12 md:col-7">
            <form onSubmit={handleSubmit(onSubmit)}>
              {/* Table */}
              <div className="mb-6 rounded-2xl shadow-xs bg-white">
                <h3 className="capitalize font-medium p-4 border-b border-gray-100">Table</h3>
                <p className="capitalize p-4 text-heading">
                  Inside - {tableDataSource?.name || ''}
                </p>
              </div>

              {/* Payment */}
              <div className="mb-6 rounded-2xl shadow-xs bg-white">
                <h3 className="capitalize font-medium p-4 border-b border-gray-100">Payment</h3>

                <div className="payment-fieldset">
                  {paymentGatewayOptionList
  .filter((gw) => !EXCLUDED_GATEWAY_SLUGS.has(gw.slug))
  .filter((gw) => gw.slug !== CREDIT_GATEWAY_SLUG || creditAllowed)
                    .map((gw, idx) => (
                      <label className="payment-label" htmlFor={`gw-${idx}`} key={gw._id}>
                        <Controller
                          control={control}
                          rules={{ required: true }}
                          name="payment_method_id"
                          render={({ field }) => (
                            <input
                              {...field}
                              id={`gw-${idx}`}
                              type="radio"
                              value={gw._id}
                                disabled={gw.slug === CREDIT_GATEWAY_SLUG && !creditAllowed}   
                              onChange={() => handleOnChange('payment_method_id', gw._id)}
                              checked={String(field.value || paymentMethodId) === String(gw._id)}
                            />
                          )}
                        />
                        <img
                          src={gw.icon_url || '/assets/images/payment-gateway/razorpay.png'}
                          alt={gw.name}
                        />
                        <span>
                          {gw.name}
                          {gw.slug === CREDIT_GATEWAY_SLUG && creditAllowed
                            ? ` ($${numericBalance.toFixed(2)})`
                            : ''}
                        </span>
                      </label>
                    ))}
                </div>

                {paymentMethodSlug === 'raams' && !isCreditPayment && (
                  <div className="mt-4">
                    <label className="db-field-title required block mb-2">Raams Gateway</label>
                    {isRaamsGatewayLoading ? (
                      <p className="text-sm text-gray-500">Loading Raams gateways...</p>
                    ) : (
                      <div className="payment-fieldset">
                        {raamsGateways.map((gateway, idx) => (
                          <label className="payment-label" htmlFor={`raams-gw-${idx}`} key={gateway.id}>
                            <Controller
                              control={control}
                              name="raams_gateway_id"
                              rules={{ required: paymentMethodSlug === 'raams' }}
                              render={({ field }) => (
                                <input
                                  {...field}
                                  id={`raams-gw-${idx}`}
                                  type="radio"
                                  value={gateway.id}
                                  onChange={() => handleRaamsGatewayChange(gateway.id)}
                                  checked={String(field.value || raamsGatewayId) === String(gateway.id)}
                                />
                              )}
                            />
                            <span>{gateway.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Phone field (hidden for credit) */}
                {!isCreditPayment && (
                  <>
                    {paymentMethodSlug === 'premier_wallets' ? (
                      <Controller
                        control={control}
                        name="account_number"
                        rules={{
                          required: true,
                          validate: (v) =>
                            isNineDigits(sanitizeAccountNumber(v)) || '9 digits',
                        }}
                        render={({ field }) => (
                          <InputSuffix
                            {...field}
                            value={field.value || ''}
                            labelStyleClassName="db-field-title required"
                            size="sm"
                            placeholder="Enter Mobile Number"
                            labelText="Mobile Number"
                            suffixText="00252"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={9}
                            onChange={(e) => {
                              const normalized = sanitizeAccountNumber(e.target.value);
                              field.onChange(normalized);
                              setValue('account_number', normalized);
                              dispatch(updateOrderTable({ account_number: normalized }));
                            }}
                            errorData={{ minLength: '9', maxLength: '9' }}
                            errorType={errors?.account_number?.type}
                          />
                        )}
                      />
                    ) : (
                      <Controller
                        control={control}
                        name="account_number"
                        rules={{
                          required: true,
                          validate: (v) =>
                            isNineDigits(sanitizeAccountNumber(v)) || '9 digits',
                        }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            value={field.value || ''}
                            labelStyleClassName="db-field-title required"
                            size="sm"
                            placeholder="Enter Mobile Number"
                            labelText="Mobile Number"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={9}
                            onChange={(e) => {
                              const normalized = sanitizeAccountNumber(e.target.value);
                              field.onChange(normalized);
                              setValue('account_number', normalized);
                              dispatch(updateOrderTable({ account_number: normalized }));
                            }}
                            errorData={{ minLength: '9', maxLength: '9' }}
                            errorType={errors?.account_number?.type}
                          />
                        )}
                      />
                    )}

                    <span>
                      Faldan numberka gali 0 ka tag <br />
                      {paymentMethodSlug === 'edahab' ? (
                        <>
                          0629018584 ❌ <br />
                          629018584 ✔️
                        </>
                      ) : (
                        <>
                          0619018584 ❌ <br />
                          619018584 ✔️
                        </>
                      )}
                    </span>
                  </>
                )}
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isFormSumbmit || isLoading}
                className="db-btn w-full rounded-3xl capitalize font-medium leading-6 py-3 text-white bg-primary"
              >
                Place Order
                {isFormSumbmit && (
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline-block"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12H4zm2
                        5.291A7.962 7.962 0 014 12H0c0 3.042
                        1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                )}
              </button>
            </form>
          </div>

          {/* Right */}
          <div className="col-12 md:col-5">
            <CartProductListSidebar title="Cart Summary" addTableOrder={addTableOrder} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default CheckoutPage;
